import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { Auth } from 'aws-amplify'
import queryClient from 'src/query-client'
import appRoutes from 'modules/common/app-routes'
import queries from './queries'

export const TOKEN_KEY = 'auth.token'
export const CLIENT_ID_KEY = 'client.id'
export const CPA_ORG_ID_KEY = 'org.id'
export const REDIRECT_URL_KEY = 'redirect.url'
export const REFERRAL_KEY = 'referral'
export const LAST_SELECTED_TAX_YEAR_KEY = 'last.tax.year'
export const SHOW_REDIRECT_MESSAGE_KEY = 'show.redirect'
export const AUTO_OPEN_IMPORT = 'auto.open.import'
export const IS_WEALTHICA = 'wealthica'
export const COGNITO_ACCESS_TOKEN_PATTERN_KEY = `CognitoIdentityServiceProvider.${process.env.NEXT_PUBLIC_COGNITO_WEB_CLIENT_ID}.{ACCOUNT}.accessToken`
export const COGNITO_ID_TOKEN_PATTERN_KEY = `CognitoIdentityServiceProvider.${process.env.NEXT_PUBLIC_COGNITO_WEB_CLIENT_ID}.{ACCOUNT}.idToken`
export const COGNITO_LAST_USER_PATTERN_KEY = `CognitoIdentityServiceProvider.${process.env.NEXT_PUBLIC_COGNITO_WEB_CLIENT_ID}.LastAuthUser`

const NO_COGNITO_CONTEXT_URLS = ['/wealthica']

const forgetStorageItem = (key: string) => {
	if (typeof window !== 'undefined') {
		localStorage.removeItem(key)
	}
}

const getStorageItem = (key: string): string | undefined | null => {
	return typeof window !== 'undefined' ? localStorage.getItem(key) : undefined
}

const setStorageItem = (key: string, value: string) => {
	localStorage.setItem(key, value)
}

const logout = (rememberUrl?: boolean) =>
	setTimeout(async () => {
		const isWealthica = getStorageItem(IS_WEALTHICA)
		if (!isWealthica) {
			queryClient.removeQueries({
				predicate: query => query.queryKey !== queries.wealthicaUserCredentials,
			})
		}
		if (rememberUrl) {
			setStorageItem(REDIRECT_URL_KEY, window.location.href)
		}
		forgetStorageItem(CLIENT_ID_KEY)
		forgetStorageItem(CPA_ORG_ID_KEY)
		forgetStorageItem(LAST_SELECTED_TAX_YEAR_KEY)
		await Auth.signOut()

		if (isWealthica) {
			queryClient.invalidateQueries()
		}
	}, 2000)

const createClient = (baseURL: string): AxiosInstance => {
	const client = axios.create({
		baseURL,
	})

	client.interceptors.request.use(async (requestConfig: AxiosRequestConfig) => {
		if (requestConfig.url && NO_COGNITO_CONTEXT_URLS.includes(requestConfig.url)) {
			return requestConfig
		}

		const session = await Auth.currentSession()
		if (requestConfig.url?.includes('/signed-url')) {
			const idToken = session.getIdToken().getJwtToken()
			if (idToken) (requestConfig.headers as any).Authorization = `Bearer ${idToken}`
		} else {
			const token = session.getAccessToken().getJwtToken()
			if (token) (requestConfig.headers as any).Authorization = `Bearer ${token}`
			const clientId = getStorageItem(CLIENT_ID_KEY)
			if (clientId) {
				requestConfig.headers!['client-id'] = clientId
			}
		}

		return requestConfig
	})

	client.interceptors.response.use(undefined, (error: AxiosError) => {
		if (error.response?.status === 401) {
			logout(true)
			return
		}

		if (error.response?.status === 403 && !!getStorageItem(CLIENT_ID_KEY)) {
			forgetStorageItem(CLIENT_ID_KEY)
			setStorageItem(SHOW_REDIRECT_MESSAGE_KEY, 'true')
			window.location.href = appRoutes.organization()
			return
		}

		throw error
	})

	return client
}

export default {
	createClient,
	forgetStorageItem,
	getStorageItem,
	setStorageItem,
	logout,
}
